import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHead } from "../components/Motion";
import { Frame } from "../components/Bits";
import { useLenis, scrollToId } from "../components/SmoothScroll";
import { ClientStrip, FaqBlock, FinalCta, FinishTiers, HowItWorks, SliderShowcase, WarrantyModule } from "../components/Sections";
import { QuoteDrivers, WalkthroughSection } from "./ServicePage";
import { COMMERCIAL_VERTICALS, RES_SERVICES } from "../data/services";
import { FAQS, PROJECTS, img } from "../data/site";

const RES_CARDS = [
  { title: "Interior", line: "Walls, ceilings, trim, doors and stairwells.", to: "/residential/interior", image: img.livingWide },
  { title: "Exterior", line: "Siding, trim, doors and decks — built for Ontario weather.", to: "/residential/exterior", image: img.exterior },
  { title: "Cabinets & Condos", line: "Factory-smooth cabinets. Condo rules handled.", to: "/residential/cabinets", image: img.kitchen },
];

export function ResidentialHub() {
  return (
    <>
      <Seo title="Residential Painting Toronto & GTA | Interior, Exterior, Cabinets | Amora Painting" description="Dust-free residential painting for Toronto and GTA homes, condos and cottages. Interior, exterior, cabinet refinishing, colour consultation and low-VOC options." />
      <PageHero eyebrow="Residential" lines={["Your home,", "painted like", "it's ours."]} intro="Interior painting, exterior painting, cabinet refinishing, condos and multi-unit homes — with custom colour consultation and low-VOC options. Done dust-free, on the timeline promised." image={img.livingTall} alt="Bright, freshly painted family living room" />
      <section className="bg-paper py-20 sm:py-28" data-testid="residential-cards">
        <div className="container-x grid gap-5 md:grid-cols-3">
          {RES_CARDS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <Link to={c.to} className="group block" data-testid={`res-card-${c.title.toLowerCase().split(" ")[0]}`}>
                <Frame src={c.image} alt={`${c.title} painting`} className="aspect-[4/5] rounded-md" />
                <div className="mt-5 flex items-start justify-between gap-4">
                  <div><h2 className="text-3xl font-extrabold tracking-tight text-navy">{c.title}</h2><p className="mt-2 text-slate-600">{c.line}</p></div>
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-navy/20 transition-colors group-hover:border-flame group-hover:bg-flame"><ArrowUpRight className="h-5 w-5 text-navy" /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="bg-white py-20 sm:py-28" data-testid="residential-services-list">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4"><SectionHead eyebrow="Services" title="Everything your home needs." /></div>
          <div className="grid gap-px overflow-hidden rounded-md bg-slate-200 sm:grid-cols-2 lg:col-span-8">
            {RES_SERVICES.map((s) => (
              <Reveal key={s.title} className="bg-white p-7"><h3 className="flex items-center gap-3 text-lg font-bold text-navy"><Check className="h-5 w-5 text-flame-600" />{s.title}</h3><p className="mt-2 pl-8 text-sm text-slate-600">{s.line}</p></Reveal>
            ))}
          </div>
        </div>
      </section>
      <HowItWorks />
      <SliderShowcase items={PROJECTS.filter((p) => p.track === "Residential").slice(0, 2)} />
      <FinishTiers />
      <WarrantyModule />
      <FaqBlock faqs={FAQS} />
      <FinalCta />
    </>
  );
}

const VERTICAL_FORM_LABEL = { offices: "Offices", hospitality: "Hospitality", retail: "Retail", "high-rise": "High-Rise & Multi-Unit", "property-managers": "Property Managers" };

const COMMERCIAL_FAQ = [
  { q: "Do you work after hours?", a: "Yes. Nights, weekends and phased scheduling are standard on commercial projects, itemized in your quote." },
  { q: "How fast do we hear back?", a: "A project lead will connect with you within 24 hours of your walkthrough request. Written quotes follow the site walkthrough." },
  { q: "What warranty do you offer?", a: "Commercial work carries a 1-year limited workmanship warranty, written into every quote." },
  { q: "Where do you work?", a: "Commercial projects anywhere in Ontario, with crews based in the GTA." },
];

export function CommercialHub() {
  const [vertical, setVertical] = useState("");
  const lenis = useLenis();
  const request = (id) => { setVertical(VERTICAL_FORM_LABEL[id]); setTimeout(() => scrollToId(lenis, "walkthrough"), 50); };
  return (
    <>
      <Seo title="Commercial Painting Contractor Ontario | Offices, Hospitality, High-Rise | Amora Painting" description="Commercial painting across Ontario for offices, hospitality, retail, high-rise and multi-unit buildings. Trusted by teams at RBC, TD Bank, Tim Hortons and SickKids Hospital." />
      <PageHero eyebrow="Commercial" lines={["Business keeps", "running. We", "keep painting."]} intro="Offices, hospitality, retail, high-rise and multi-unit buildings across Ontario. After-hours scheduling, a dust-free process and one accountable project lead from walkthrough to warranty." image={img.officeHall} alt="Bright office corridor with freshly painted walls" cta={false}>
        <Reveal delay={0.45} className="mt-9"><a href="#walkthrough" className="btn-primary" data-testid="commercial-hero-walkthrough-btn">Request a Site Walkthrough</a></Reveal>
      </PageHero>
      <ClientStrip />
      <section className="bg-paper py-20 sm:py-28" data-testid="commercial-verticals">
        <div className="container-x">
          <SectionHead eyebrow="Who we work for" title="Built for the way your space operates." />
          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-6">
            {COMMERCIAL_VERTICALS.map((v, i) => (
              <Reveal key={v.id} delay={i * 0.06} className={`flex flex-col overflow-hidden rounded-md border border-slate-200 bg-white ${i < 2 ? "lg:col-span-3" : "lg:col-span-2"}`}>
                <Frame src={v.image} alt={`${v.title} commercial painting`} className="aspect-[16/10]" />
                <div className="flex flex-1 flex-col p-7">
                  <h3 className="text-2xl font-extrabold text-navy">{v.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{v.line}</p>
                  <div className="mt-6 flex flex-wrap items-center gap-4">
                    <button type="button" onClick={() => request(v.id)} className="btn-primary !px-5 !py-3" data-testid={`vertical-${v.id}-walkthrough-btn`}>Request a Site Walkthrough</button>
                    {v.to && <Link to={v.to} className="text-sm font-bold text-navy underline decoration-flame decoration-2 underline-offset-4" data-testid={`vertical-${v.id}-link`}>Learn more</Link>}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <QuoteDrivers />
      <HowItWorks />
      <WarrantyModule />
      <WalkthroughSection vertical={vertical} />
      <FaqBlock faqs={COMMERCIAL_FAQ} />
    </>
  );
}
